/* ============================================================
 * services/api.js — Cliente do backend (FastAPI)
 * Login por tabela de usuários (JWT) + leitura dos contratos
 * da pasta local. Base configurável via VITE_API_URL.
 * ============================================================ */
// Dev: VITE_API_URL indefinido → http://localhost:8000.
// Produção: VITE_API_URL="" (vazio) → chamadas relativas /api na mesma origem (via nginx).
const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

const token = () => localStorage.getItem('bkr-token') || '';

async function apiLogin(email, senha) {
  const r = await fetch(`${BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, senha }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `Falha no login (${r.status})`);
  return data; // { token, user }
}

async function apiMe() {
  const r = await fetch(`${BASE}/api/auth/me`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha (${r.status})`);
  return (await r.json()).user; // { nome, email, papel }
}

async function apiContracts() {
  const r = await fetch(`${BASE}/api/contracts`, { headers: { Authorization: `Bearer ${token()}` } });
  if (r.status === 401) throw new Error('Sessão expirada — entre novamente.');
  if (!r.ok) throw new Error(`Falha ao listar contratos (${r.status})`);
  return r.json(); // { pasta, existe, arquivos: [...] }
}

async function apiLoginMicrosoft(idToken) {
  const r = await fetch(`${BASE}/api/auth/microsoft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `Falha no login (${r.status})`);
  return data; // { token, user }
}

async function apiContractsData() {
  const r = await fetch(`${BASE}/api/contracts/data`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao carregar dados dos contratos (${r.status})`);
  return r.json(); // { extraidoEm, total, clientes, contratos }
}

async function apiEditarContrato(dbId, campos) {
  const r = await fetch(`${BASE}/api/contracts/${dbId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify(campos),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `Falha ao salvar (${r.status})`);
  return data; // { cliente, contrato }
}

async function apiEncerrarContrato(dbId, motivo) {
  const r = await fetch(`${BASE}/api/contracts/${dbId}/encerrar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ motivo: motivo || null }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao encerrar (${r.status})`);
  return d;
}
async function apiReativarContrato(dbId) {
  const r = await fetch(`${BASE}/api/contracts/${dbId}/reativar`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao reativar (${r.status})`);
  return d;
}
async function apiConfirmarValor(dbId) {
  const r = await fetch(`${BASE}/api/contracts/${dbId}/confirmar-valor`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao confirmar valor (${r.status})`);
  return d;
}

async function apiUsersList() {
  const r = await fetch(`${BASE}/api/users`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao listar acessos (${r.status})`);
  return r.json();
}

async function apiUserAprovar(id, papel) {
  const r = await fetch(`${BASE}/api/users/${id}/aprovar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ papel }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao aprovar (${r.status})`);
  return d;
}

async function apiUserRevogar(id) {
  const r = await fetch(`${BASE}/api/users/${id}/revogar`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao revogar (${r.status})`);
  return d;
}

async function apiUserPapel(id, papel) {
  const r = await fetch(`${BASE}/api/users/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ papel }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao mudar papel (${r.status})`);
  return d;
}

async function apiAcessosLogs() {
  const r = await fetch(`${BASE}/api/acessos/logs`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao carregar logs (${r.status})`);
  return r.json();
}
async function apiAcessosPendentes() {
  try {
    const r = await fetch(`${BASE}/api/acessos/pendentes`, { headers: { Authorization: `Bearer ${token()}` } });
    return r.ok ? r.json() : { pendentes: 0 };
  } catch { return { pendentes: 0 }; }
}

async function apiRateio(contratoDbId) {
  const r = await fetch(`${BASE}/api/contracts/${contratoDbId}/rateio`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao carregar rateio (${r.status})`);
  return r.json(); // { linhas, ativos, total }
}

// ---------- Aditivos (entidade própria) ----------
async function apiAditivos() {
  const r = await fetch(`${BASE}/api/aditivos`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao listar aditivos (${r.status})`);
  return (await r.json()).aditivos || [];
}
async function apiAditivosDoContrato(baseId) {
  const r = await fetch(`${BASE}/api/contracts/${baseId}/aditivos`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha (${r.status})`);
  return (await r.json()).aditivos || [];
}
async function apiAditivoVincular(id, baseId) {
  const r = await fetch(`${BASE}/api/aditivos/${id}/vinculo`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify({ base_id: baseId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao vincular (${r.status})`);
  return d;
}
async function apiAditivoAplicar(id) {
  const r = await fetch(`${BASE}/api/aditivos/${id}/aplicar`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao aplicar (${r.status})`);
  return d;
}
async function apiAditivoExcluir(id) {
  const r = await fetch(`${BASE}/api/aditivos/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao excluir (${r.status})`);
  return d;
}
async function abrirAditivo(nome) {
  const r = await fetch(`${BASE}/api/aditivos/${encodeURIComponent(nome)}/download`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao abrir (${r.status})`);
  const blob = await r.blob(); const url = URL.createObjectURL(blob);
  window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ---------- Quadro Societário (navegador da pasta no SharePoint) ----------
async function apiQuadroSocietario() {
  const r = await fetch(`${BASE}/api/quadro-societario`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao carregar (${r.status})`);
  return r.json(); // { configurado, pastaUrl, itens: [...] }
}
async function apiQuadroSocietarioPasta(itemId) {
  const r = await fetch(`${BASE}/api/quadro-societario/pasta/${encodeURIComponent(itemId)}`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao abrir a pasta (${r.status})`);
  return (await r.json()).itens || [];
}

async function apiConformidade(contratoDbId) {
  const r = await fetch(`${BASE}/api/contracts/${contratoDbId}/conformidade`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao carregar conformidade (${r.status})`);
  return r.json(); // { totalEmpresas, empresas, alvaras, certificados }
}

async function apiRateioSugestao(contratoDbId) {
  const r = await fetch(`${BASE}/api/contracts/${contratoDbId}/rateio/sugestao`, { headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao ler o contrato (${r.status})`);
  return d; // { linhas, total, encontradas, extraidas }
}

async function apiRateioDefinir(contratoDbId, data, linhas, substituirTudo = false) {
  const r = await fetch(`${BASE}/api/contracts/${contratoDbId}/rateio`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ data, linhas, substituir_tudo: substituirTudo }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao salvar rateio (${r.status})`);
  return d;
}

async function apiRateioReajustar(contratoDbId, data, percentual) {
  const r = await fetch(`${BASE}/api/contracts/${contratoDbId}/rateio/reajustar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ data, percentual }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao reajustar (${r.status})`);
  return d;
}

async function apiEmpresasList() {
  const r = await fetch(`${BASE}/api/empresas`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao listar empresas (${r.status})`);
  return r.json();
}

async function baixarArquivo(path, nomeArquivo) {
  const r = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao baixar (${r.status})`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nomeArquivo; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function apiUpload(files) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  const r = await fetch(`${BASE}/api/contracts/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `Falha no upload (${r.status})`);
  return data; // { total, ok, resultados }
}

async function apiEmpresasImport(file) {
  const fd = new FormData();
  fd.append('file', file);
  const r = await fetch(`${BASE}/api/empresas/import`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `Falha ao importar (${r.status})`);
  return data;
}

async function apiEmpresasSharepoint() {
  const r = await fetch(`${BASE}/api/empresas/sharepoint`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) return { configurado: false, pastaUrl: null };
  return r.json(); // { configurado, pastaUrl, arquivo }
}

async function apiEmpresasSincronizar() {
  const r = await fetch(`${BASE}/api/empresas/sincronizar`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao sincronizar (${r.status})`);
  return d;
}

// Baixa a tabela de empresas em .xlsx (a rota exige JWT, então buscamos o
// blob com o header e disparamos o download via <a download> temporário).
// `ids` (opcional) restringe às linhas visíveis na tela — o recorte por
// filtro é feito no frontend e só os ids das linhas mostradas vão pro backend.
async function apiEmpresasExport(ids) {
  const qs = (ids && ids.length) ? `?ids=${ids.join(',')}` : '';
  const r = await fetch(`${BASE}/api/empresas/export${qs}`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao exportar (${r.status})`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'empresas_booker.xlsx';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function apiEmpresaVincular(id, contratoId) {
  const r = await fetch(`${BASE}/api/empresas/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ contrato_id: contratoId }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `Falha ao vincular (${r.status})`);
  return data;
}

async function apiEmpresaCriar(body) {
  const r = await fetch(`${BASE}/api/empresas`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao cadastrar (${r.status})`);
  return d;
}
async function apiEmpresaEditar(id, campos) {
  const r = await fetch(`${BASE}/api/empresas/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify(campos),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao salvar (${r.status})`);
  return d;
}

async function abrirArquivo(nome) {
  const r = await fetch(`${BASE}/api/contracts/${encodeURIComponent(nome)}/download`, {
    headers: { Authorization: `Bearer ${token()}` },
  });
  if (!r.ok) throw new Error(`Falha ao abrir o arquivo (${r.status})`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}


async function apiExcluirContrato(nome) {
  const r = await fetch(`${BASE}/api/contracts/${encodeURIComponent(nome)}/excluir`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao excluir (${r.status})`);
  return d; // { ok, contratoRemovido, pdfRemovido, empresasDesvinculadas }
}

async function apiAlvaras() {
  const r = await fetch(`${BASE}/api/alvaras`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao listar alvarás (${r.status})`);
  return r.json(); // { alvaras: [...] }
}

async function apiAlvarasCobertura() {
  const r = await fetch(`${BASE}/api/alvaras/cobertura`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao carregar cobertura (${r.status})`);
  return r.json(); // { empresas: [...], orfaos: [...] }
}
async function apiAlvarasUpload(files) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  const r = await fetch(`${BASE}/api/alvaras/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `Falha no upload (${r.status})`);
  return data;
}

async function apiEditarAlvara(id, campos) {
  const r = await fetch(`${BASE}/api/alvaras/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify(campos),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao salvar (${r.status})`);
  return d;
}

async function apiAlvaraObsList(id) {
  const r = await fetch(`${BASE}/api/alvaras/${id}/observacoes`, { headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao carregar observações (${r.status})`);
  return d.observacoes || [];
}

async function apiAlvaraObsAdd(id, texto) {
  const r = await fetch(`${BASE}/api/alvaras/${id}/observacoes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
    body: JSON.stringify({ texto }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao adicionar (${r.status})`);
  return d.observacoes || [];
}

async function apiAlvaraObsDel(obsId) {
  const r = await fetch(`${BASE}/api/alvaras/observacoes/${obsId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao excluir (${r.status})`);
  return d;
}

async function apiExcluirAlvara(nome) {
  const r = await fetch(`${BASE}/api/alvaras/${encodeURIComponent(nome)}/excluir`, {
    method: 'DELETE', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao excluir (${r.status})`);
  return d;
}

async function abrirAlvara(nome) {
  const r = await fetch(`${BASE}/api/alvaras/${encodeURIComponent(nome)}/download`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao abrir (${r.status})`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ---------- Certificados ----------
async function apiCertificados() {
  const r = await fetch(`${BASE}/api/certificados`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao listar certificados (${r.status})`);
  return (await r.json()).certificados || [];
}
async function apiCertImport(file) {
  const fd = new FormData(); fd.append('file', file);
  const r = await fetch(`${BASE}/api/certificados/import`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao importar (${r.status})`);
  return d;
}
async function apiCertificadosSharepoint() {
  const r = await fetch(`${BASE}/api/certificados/sharepoint`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) return { configurado: false, pastaUrl: null };
  return r.json(); // { configurado, pastaUrl, arquivo }
}
async function apiCertificadosSincronizar() {
  const r = await fetch(`${BASE}/api/certificados/sincronizar`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao sincronizar (${r.status})`);
  return d;
}
async function apiEditarCertificado(id, campos) {
  const r = await fetch(`${BASE}/api/certificados/${id}`, {
    method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify(campos),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao salvar (${r.status})`);
  return d;
}
async function apiExcluirCertificado(id) {
  const r = await fetch(`${BASE}/api/certificados/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao excluir (${r.status})`);
  return d;
}
async function apiCertObsList(id) {
  const r = await fetch(`${BASE}/api/certificados/${id}/observacoes`, { headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao carregar (${r.status})`);
  return d.observacoes || [];
}
async function apiCertObsAdd(id, texto) {
  const r = await fetch(`${BASE}/api/certificados/${id}/observacoes`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify({ texto }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao adicionar (${r.status})`);
  return d.observacoes || [];
}
async function apiCertObsDel(obsId) {
  const r = await fetch(`${BASE}/api/certificados/observacoes/${obsId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao excluir (${r.status})`);
  return d;
}

async function apiHealth() {
  try { const r = await fetch(`${BASE}/api/health`); return r.ok ? await r.json() : null; }
  catch { return null; }
}

async function apiSharepointStatus() {
  try {
    const r = await fetch(`${BASE}/api/sharepoint/status`, { headers: { Authorization: `Bearer ${token()}` } });
    return r.ok ? await r.json() : null;
  } catch { return null; }
}

async function apiSharepointPendentes(tipo = 'contratos') {
  try {
    const r = await fetch(`${BASE}/api/sharepoint/pendentes?tipo=${encodeURIComponent(tipo)}`, { headers: { Authorization: `Bearer ${token()}` } });
    return r.ok ? await r.json() : null;
  } catch { return null; }
}

async function apiSharepointImportar(itemId, force = false) {
  const r = await fetch(`${BASE}/api/sharepoint/importar/${encodeURIComponent(itemId)}${force ? '?force=true' : ''}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (r.status === 409) { const e = new Error(d.detail || 'Possível duplicado'); e.duplicado = true; throw e; }
  if (!r.ok) throw new Error(d.detail || `Falha ao importar (${r.status})`);
  return d;
}

async function apiSharepointVincularExistente(itemId) {
  const r = await fetch(`${BASE}/api/sharepoint/vincular-existente/${encodeURIComponent(itemId)}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao vincular (${r.status})`);
  return d;
}

async function apiSharepointSincronizar(tipo = 'contratos') {
  const r = await fetch(`${BASE}/api/sharepoint/sincronizar?tipo=${encodeURIComponent(tipo)}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao sincronizar (${r.status})`);
  return d;
}

async function apiSharepointIgnorar(itemId) {
  const r = await fetch(`${BASE}/api/sharepoint/ignorar/${encodeURIComponent(itemId)}`, {
    method: 'POST', headers: { Authorization: `Bearer ${token()}` },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || `Falha ao dispensar (${r.status})`);
  return d;
}

async function abrirSharepoint(itemId) {
  const r = await fetch(`${BASE}/api/sharepoint/arquivo/${encodeURIComponent(itemId)}`, { headers: { Authorization: `Bearer ${token()}` } });
  if (!r.ok) throw new Error(`Falha ao abrir (${r.status})`);
  const blob = await r.blob(); const url = URL.createObjectURL(blob);
  window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const Api = { apiLogin, apiMe, apiLoginMicrosoft, apiContracts, apiContractsData, apiEditarContrato, apiEncerrarContrato, apiReativarContrato, apiConfirmarValor, apiRateio, apiConformidade, apiAditivos, apiAditivosDoContrato, apiAditivoVincular, apiAditivoAplicar, apiAditivoExcluir, abrirAditivo, apiQuadroSocietario, apiQuadroSocietarioPasta, apiRateioSugestao, apiRateioDefinir, apiRateioReajustar, apiUpload, apiUsersList, apiUserAprovar, apiUserRevogar, apiUserPapel, apiAcessosLogs, apiAcessosPendentes, apiEmpresasList, apiEmpresaVincular, apiEmpresaCriar, apiEmpresaEditar, apiEmpresasImport, apiEmpresasSincronizar, apiEmpresasSharepoint, apiEmpresasExport, baixarArquivo, abrirArquivo, apiExcluirContrato, apiAlvaras, apiAlvarasCobertura, apiAlvarasUpload, apiEditarAlvara, apiExcluirAlvara, abrirAlvara, apiAlvaraObsList, apiAlvaraObsAdd, apiAlvaraObsDel, apiCertificados, apiCertImport, apiCertificadosSharepoint, apiCertificadosSincronizar, apiEditarCertificado, apiExcluirCertificado, apiCertObsList, apiCertObsAdd, apiCertObsDel, apiHealth, apiSharepointStatus, apiSharepointPendentes, abrirSharepoint, apiSharepointImportar, apiSharepointVincularExistente, apiSharepointIgnorar, apiSharepointSincronizar, API_BASE: BASE };
if (typeof window !== 'undefined') window.Api = Api;

export { apiLogin, apiMe, apiLoginMicrosoft, apiContracts, apiContractsData, apiEditarContrato, apiEncerrarContrato, apiReativarContrato, apiConfirmarValor, apiRateio, apiConformidade, apiAditivos, apiAditivosDoContrato, apiAditivoVincular, apiAditivoAplicar, apiAditivoExcluir, abrirAditivo, apiQuadroSocietario, apiQuadroSocietarioPasta, apiRateioSugestao, apiRateioDefinir, apiRateioReajustar, apiUpload, apiUsersList, apiUserAprovar, apiUserRevogar, apiUserPapel, apiAcessosLogs, apiAcessosPendentes, apiEmpresasList, apiEmpresaVincular, apiEmpresaCriar, apiEmpresaEditar, apiEmpresasImport, apiEmpresasSincronizar, apiEmpresasSharepoint, apiEmpresasExport, baixarArquivo, abrirArquivo, apiExcluirContrato, apiAlvaras, apiAlvarasCobertura, apiAlvarasUpload, apiEditarAlvara, apiExcluirAlvara, abrirAlvara, apiAlvaraObsList, apiAlvaraObsAdd, apiAlvaraObsDel, apiCertificados, apiCertImport, apiCertificadosSharepoint, apiCertificadosSincronizar, apiEditarCertificado, apiExcluirCertificado, apiCertObsList, apiCertObsAdd, apiCertObsDel, apiHealth, apiSharepointStatus, apiSharepointPendentes, abrirSharepoint, apiSharepointImportar, apiSharepointVincularExistente, apiSharepointIgnorar, apiSharepointSincronizar };
export const API_BASE = BASE;
